import BigNumber from 'bignumber.js';
import { get } from 'lodash';
import { BeneficialOwnerRole, BeneficialOwnerType, CompanyApplicantRelatedPartyType } from '@lendi/core-constants';
import { Accordion, AccordionGroup } from '@lendi-ui/accordion';
import { Heading } from '@lendi-ui/typography';

import {
  FIELD_PATHS,
  FIELD_VALUES,
  PII_FIELD_MASKING_CLASS,
  RELATED_PARTY_COMPANY_OWNER_TYPE_LABELS,
  YES_NO_OPTIONS,
} from '@funnels/constants';
import { useFormValues } from '@funnels/hooks';
import { useAnalytics } from '@funnels/providers';
import { RelatedParty, StoreApplicant } from '@funnels/types';
import { FunnelGrid, FunnelGridUnit } from '@funnels/ui-components';
import { HelpTooltip } from '@funnels/ui-components/_common';
import {
  capitalizeForEnumValue,
  getApplicantName,
  initialFormValues,
  validateInclusiveNumberRanges,
} from '@funnels/utils';

import { DropdownField, PercentageInputField, TextAreaField } from '../../Fields';
import UncontrolledRadioButtonsField from '../../Fields/_uncontrolled/RadioButtonsField';
import { AccordionContentWrapper } from '../../Layouts/ReadOnly/_common/style';

interface CompanyDirectorsProp {
  directors: RelatedParty[];
  companyApplicantClientId: string;
  opened: boolean[];
  setOpened: (value: boolean[]) => void;
}

export const RELATED_PARTY_OWNER_ROLE_LABELS = Object.entries(BeneficialOwnerRole).map(
  ([key, value]: [string, string]) => ({
    label: capitalizeForEnumValue(value),
    value,
  })
);

export const CompanyDirectors = ({ directors, companyApplicantClientId, opened, setOpened }: CompanyDirectorsProp) => {
  const { tracking } = useAnalytics();
  const [formValues, setFormValues] = useFormValues();
  const applicants: StoreApplicant[] = get(formValues, FIELD_PATHS.APPLICANTS(), []);
  const relatedParties: RelatedParty[] = get(formValues, FIELD_PATHS.RELATED_PARTIES(), []);

  function handleAccordionClick(index: number, title: string) {
    const arr = [...opened];
    tracking?.accordion[arr[index] ? 'collapsed' : 'expanded']({ name: title, position: title });
    arr[index] = !arr[index]; // toggle
    setOpened(arr);
  }

  const handleBeneficialOwnerUpdate = (
    e: React.ChangeEvent<HTMLInputElement>,
    directorApplicantId: string,
    companyBeneficialOwnerClientId?: string
  ) => {
    const value = e.target.value;
    if (value === FIELD_VALUES.TRUE) {
      setFormValues(FIELD_PATHS.RELATED_PARTIES(), [
        ...relatedParties,
        initialFormValues.INITIAL_COMPANY_BENEFICIAL_OWNER_RELATED_PARTY(companyApplicantClientId, directorApplicantId),
      ]);
    } else {
      setFormValues(
        FIELD_PATHS.RELATED_PARTIES(),
        relatedParties.filter(
          (_, index) =>
            get({ relatedParties }, FIELD_PATHS.RELATED_PARTY_CLIENT_ID(index)) !== companyBeneficialOwnerClientId
        )
      );
    }
  };

  const handleOnChange = (companyBeneficialOwnerIndex: number) => {
    setFormValues(FIELD_PATHS.RELATED_PARTY_OWNER_ROLE(companyBeneficialOwnerIndex), undefined);
    setFormValues(FIELD_PATHS.RELATED_PARTY_DESCRIPTION(companyBeneficialOwnerIndex), undefined);
  };

  const beneficialOwnerTooltip = `A beneficial owner is an individual that owns or controls the entity. Control may be by acting as trustee or by exercising control through some other means. 
   ‘Owns’ in this case means owning 25% or more (directly or indirectly) of the entity.
   ‘Controls’ may be by acting as trustee or by exercising control through some other means.`;

  const beneficialOwnerTypeTooltip = `There must be at least 1 controller on the application.`;

  return (
    <AccordionContentWrapper>
      <AccordionGroup variant="empty">
        {directors.map((director: RelatedParty, index) => {
          const directorApplicantId = get(
            { relatedParties: [director] },
            FIELD_PATHS.RELATED_PARTY_APPLICANT_CLIENT_ID(0)
          );
          const directorName = getApplicantName(applicants, directorApplicantId);
          const heading = directorName || `Director ${index + 1}`;
          const pii = directorName;
          const companyBeneficialOwnerIndex = relatedParties.findIndex(
            (relatedParty) =>
              get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_APPLICANT_CLIENT_ID(0)) ===
                directorApplicantId &&
              get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_COMPANY_APPLICANT_CLIENT_ID(0)) ===
                companyApplicantClientId &&
              get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_TYPE(0)) ===
                CompanyApplicantRelatedPartyType.CompanyBeneficialOwner
          );
          const companyBeneficialOwnerType = get(
            { relatedParties },
            FIELD_PATHS.RELATED_PARTY_OWNER_TYPE(companyBeneficialOwnerIndex)
          );

          return (
            <AccordionContentWrapper key={`director-${director.clientId}`}>
              <Accordion isOpen={opened[index]}>
                <Accordion.Header
                  onClick={() => handleAccordionClick(index, 'Director details')}
                  data-component="accordion-header"
                >
                  <Heading size="xxs" className={PII_FIELD_MASKING_CLASS}>
                    {heading}
                  </Heading>
                </Accordion.Header>
                <Accordion.Content data-component="accordion-content">
                  <FunnelGrid>
                    <FunnelGridUnit mb="md">
                      <UncontrolledRadioButtonsField
                        label={`Is ${heading} a beneficial owner?`}
                        piiStringInLabel={pii}
                        labelAfter={<HelpTooltip content={beneficialOwnerTooltip} />}
                        radios={YES_NO_OPTIONS}
                        direction="row"
                        onChange={(e) =>
                          handleBeneficialOwnerUpdate(
                            e,
                            directorApplicantId,
                            get({ relatedParties }, FIELD_PATHS.RELATED_PARTY_CLIENT_ID(companyBeneficialOwnerIndex))
                          )
                        }
                        selectedValue={companyBeneficialOwnerIndex !== -1}
                      />
                    </FunnelGridUnit>
                    {companyBeneficialOwnerIndex !== -1 && (
                      <>
                        <FunnelGridUnit mb="md">
                          <DropdownField
                            labelAfter={<HelpTooltip content={beneficialOwnerTypeTooltip} />}
                            label="Beneficial owner type"
                            name={FIELD_PATHS.RELATED_PARTY_OWNER_TYPE(companyBeneficialOwnerIndex)}
                            options={RELATED_PARTY_COMPANY_OWNER_TYPE_LABELS}
                            isOptional
                            onChange={() => handleOnChange(companyBeneficialOwnerIndex)}
                          />
                        </FunnelGridUnit>
                        {companyBeneficialOwnerType === BeneficialOwnerType.Controller && (
                          <FunnelGridUnit mb="md">
                            <DropdownField
                              label="Beneficial controller role"
                              name={FIELD_PATHS.RELATED_PARTY_OWNER_ROLE(companyBeneficialOwnerIndex)}
                              options={RELATED_PARTY_OWNER_ROLE_LABELS}
                              isOptional
                            />
                          </FunnelGridUnit>
                        )}
                        {companyBeneficialOwnerType === BeneficialOwnerType.Other && (
                          <FunnelGridUnit mb="md">
                            <TextAreaField
                              label="Description"
                              name={FIELD_PATHS.RELATED_PARTY_DESCRIPTION(companyBeneficialOwnerIndex)}
                              isOptional
                            />
                          </FunnelGridUnit>
                        )}
                        <FunnelGridUnit mb="md">
                          <PercentageInputField
                            name={FIELD_PATHS.RELATED_PARTY_COMPANY_BENEFICIAL_OWNER_PERCENTAGE(
                              companyBeneficialOwnerIndex
                            )}
                            label="Beneficial ownership percentage"
                            isOptional
                            isZeroValid
                            format={(value?: number) => new BigNumber(value || 0).multipliedBy(100).toNumber()}
                            parse={(value) => new BigNumber(value || 0).dividedBy(100).toNumber()}
                            validate={validateInclusiveNumberRanges(0, 99.99, true)}
                          />
                        </FunnelGridUnit>
                      </>
                    )}
                  </FunnelGrid>
                </Accordion.Content>
              </Accordion>
            </AccordionContentWrapper>
          );
        })}
      </AccordionGroup>
    </AccordionContentWrapper>
  );
};
